<template>
  <div class="analytics-section" data-testid="errors-to-fix">
    <div class="mb-4">
      <h3 class="section-title text-red-400">{{ $t('botEditor.analytics.errorsToFix.title') }}</h3>
      <p class="text-xs text-gray-400 mt-1">{{ $t('botEditor.analytics.errorsToFix.subtitle') }}</p>
    </div>
    <div class="overflow-x-auto">
    <DataTable
      v-model:expandedRows="expandedRows"
      :value="sortedRows"
      dataKey="key"
      size="small"
      :rowHover="sortedRows.length > 0"
      :rowClass="(data) => (data.maybeResolved ? 'opacity-80' : '')"
      class="text-sm"
    >
      <Column expander style="width: 3rem" />
      <Column :header="$t('botEditor.analytics.errorsToFix.columns.source')" style="width: 9rem">
        <template #body="{ data }">
          <span class="flex items-center gap-1.5 text-xs">
            <span class="material-icons select-none" style="font-size: 16px" aria-hidden="true">{{ SOURCE_ICONS[data.errorSource] }}</span>
            {{ $t('botEditor.analytics.errorsToFix.sources.' + data.errorSource) }}
          </span>
        </template>
      </Column>
      <Column :header="$t('botEditor.analytics.errorsToFix.columns.where')">
        <template #body="{ data }">
          <div class="text-xs text-gray-200">
            {{ data.sourceName || '—' }}<span v-if="data.toolName"> · {{ data.toolName }}</span>
          </div>
          <div v-if="data.nodeId" class="text-xs text-gray-400">
            {{ $t('botEditor.analytics.errorsToFix.node') }}: <span class="font-mono">{{ data.nodeType || data.nodeId }}</span>
          </div>
        </template>
      </Column>
      <Column :header="$t('botEditor.analytics.errorsToFix.columns.type')" style="width: 14rem">
        <template #body="{ data }">
          <div class="flex flex-wrap items-center gap-1">
            <Tag :value="data.errorType" severity="danger" rounded class="font-mono" />
            <Tag v-if="data.handled" :value="$t('botEditor.analytics.errorsToFix.handled')" severity="secondary" rounded v-tooltip.top="$t('botEditor.analytics.errorsToFix.handledHint')" />
            <Tag v-if="data.maybeResolved" :value="$t('botEditor.analytics.errorsToFix.maybeResolved')" severity="success" rounded v-tooltip.top="$t('botEditor.analytics.errorsToFix.maybeResolvedHint')" />
          </div>
          <div class="text-xs text-gray-400 mt-1">{{ $t('botEditor.analytics.errorsToFix.types.' + data.errorType) }}</div>
        </template>
      </Column>
      <Column :header="$t('botEditor.analytics.errorsToFix.columns.lastMessage')">
        <template #body="{ data }">
          <div v-tooltip.top="data.lastMessage" tabindex="0" class="text-xs text-gray-300 truncate rounded focus-visible:outline-2 focus-visible:outline-cyan-400" style="max-width: 20rem">{{ data.lastMessage }}</div>
        </template>
      </Column>
      <Column :header="$t('botEditor.analytics.errorsToFix.columns.channels')" style="width: 8rem">
        <template #body="{ data }">
          <span class="text-xs text-gray-400">{{ (data.channels || []).join(', ') }}</span>
        </template>
      </Column>
      <Column field="count" :header="$t('botEditor.analytics.errorsToFix.columns.count')" style="width: 5rem; text-align: center" :pt="centeredHeaderPt" />
      <Column :header="$t('botEditor.analytics.errorsToFix.columns.lastOccurrence')" style="width: 10rem; text-align: center" :pt="centeredHeaderPt">
        <template #body="{ data }">
          <span class="text-xs">{{ formatDate(data.lastOccurrence) }}</span>
        </template>
      </Column>
      <template #expansion="{ data }">
        <div class="px-4 py-2">
          <div class="flex items-center justify-between mb-2">
            <span class="text-xs font-medium text-gray-400">{{ $t('botEditor.analytics.errorsToFix.occurrencesTitle') }}</span>
            <router-link
              v-if="flowLink(data)"
              :to="flowLink(data)"
              data-testid="open-in-flow"
              class="text-xs text-cyan-400 hover:underline focus-visible:underline rounded flex items-center gap-1"
            >
              <span class="material-icons select-none" style="font-size: 14px" aria-hidden="true">account_tree</span>
              {{ $t('botEditor.analytics.errorsToFix.openInFlow') }}
            </router-link>
          </div>
          <ul class="flex flex-col gap-1">
            <li
              v-for="(occ, i) in data.occurrences"
              :key="i"
              class="flex flex-wrap items-center gap-3 text-xs text-gray-300 border-b border-gray-700 py-1"
              data-testid="occurrence"
            >
              <span class="text-gray-400">{{ formatDate(occ.timestamp) }}</span>
              <span v-if="occ.connectorType" class="text-gray-400">{{ occ.connectorType }}</span>
              <span v-if="occ.responseStatus" class="font-mono">{{ $t('botEditor.analytics.errorsToFix.responseStatus') }}: {{ occ.responseStatus }}</span>
              <span tabindex="0" class="truncate flex-1 min-w-0 text-gray-300 rounded focus-visible:outline-2 focus-visible:outline-cyan-400" v-tooltip.top="occ.errorMessage">{{ occ.errorMessage }}</span>
              <router-link
                v-if="occ.conversationId"
                :to="conversationLink(occ)"
                data-testid="open-conversation"
                class="text-cyan-400 hover:underline"
              >{{ $t('botEditor.analytics.errorsToFix.openConversation') }}</router-link>
              <span v-else-if="occ.executionId" class="flex items-center gap-2" data-testid="execution-id">
                <span class="text-gray-400">{{ $t('botEditor.analytics.errorsToFix.webhookCall') }}</span>
                <span class="font-mono">{{ occ.executionId }}</span>
                <button
                  type="button"
                  class="p-1 -m-1 rounded text-gray-400 hover:text-gray-200 cursor-pointer focus-visible:outline-2 focus-visible:outline-cyan-400"
                  :aria-label="$t('botEditor.analytics.errorsToFix.copyExecutionId')"
                  v-tooltip.top="copiedIds[occ.executionId] ? $t('botEditor.analytics.errorsToFix.copied') : $t('botEditor.analytics.errorsToFix.copyExecutionId')"
                  @click="handleCopy(occ.executionId)"
                >
                  <span class="material-icons select-none" style="font-size: 14px" aria-hidden="true">{{ copiedIds[occ.executionId] ? 'check' : 'content_copy' }}</span>
                </button>
              </span>
            </li>
          </ul>
        </div>
      </template>
      <template #empty>
        <div class="text-center py-8 text-gray-400 text-sm">
          <span class="material-icons text-green-500 select-none" aria-hidden="true">check_circle</span>
          <p>{{ $t('botEditor.analytics.errorsToFix.emptyState') }}</p>
        </div>
      </template>
    </DataTable>
    </div>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue';
import { copyToClipboard } from '../../utils/clipboard';

const props = defineProps({
  rows: { type: Array, default: () => [] },
  workspaceId: { type: String, default: '' },
  botId: { type: String, default: '' },
  productionBotId: { type: String, default: null },
});

const SOURCE_ICONS = { tool: 'build', flow: 'account_tree', webhook: 'webhook', turn: 'forum' };
const centeredHeaderPt = { columnHeaderContent: { class: 'justify-center' } };

const expandedRows = ref({});
const copiedIds = ref({});

// Da sistemare prima (per frequenza), le probabilmente risolte in fondo.
const sortedRows = computed(() => {
  const rows = props.rows || [];
  return [
    ...rows.filter((r) => !r.maybeResolved).sort((a, b) => b.count - a.count),
    ...rows.filter((r) => r.maybeResolved).sort((a, b) => b.count - a.count),
  ];
});

const formatDate = (d) => (d ? new Date(d).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : '');

const conversationLink = (occ) => ({
  name: 'BotTranscripts',
  params: { workspaceId: props.workspaceId, id: props.botId },
  query: {
    conversation: occ.conversationId,
    ...(occ.messageId ? { message: occ.messageId } : {}),
    source: props.productionBotId && occ.botId === props.productionBotId ? 'production' : 'test',
  },
});

const FLOW_ROUTES = {
  flow: { name: 'FlowEditor', param: 'flowId' },
  tool: { name: 'ToolFlowEditor', param: 'toolFlowId' },
  webhook: { name: 'WebhookFlowEditor', param: 'webhookFlowId' },
};

const flowLink = (row) => {
  const wf = row.workingFlow;
  const target = wf && FLOW_ROUTES[wf.kind];
  if (!target) return null;
  return {
    name: target.name,
    params: { workspaceId: props.workspaceId, id: props.botId, [target.param]: wf.id },
    ...(row.nodeId ? { query: { node: row.nodeId } } : {}),
  };
};

const handleCopy = async (id) => {
  await copyToClipboard(id);
  copiedIds.value = { ...copiedIds.value, [id]: true };
  setTimeout(() => {
    const next = { ...copiedIds.value };
    delete next[id];
    copiedIds.value = next;
  }, 1500);
};
</script>
